import React from "react";
import {
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import {
  SceneShell,
  SlideChip,
  SlideTitle,
  Card3D,
  Punchline,
  DefBox,
  PlainBox,
  Pill,
  SnapshotFrame,
  BrandLogo,
  glow,
  spAppear,
} from "./shared";

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/* ════════════ Slide 25 — The honest gap ════════════ */
export const S25Gap: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonOrange;
  const ap = spAppear(frame, fps);

  const bubbles = [
    { t: "How does retrieval work?", me: true },
    { t: "It searches your KB, then answers with citations…", me: false },
    { t: "Great — and what about chunking?", me: true },
  ];
  const voidX = 432;
  const voidY = 600;
  const pulse = 1 + Math.sin(frame * 0.07) * 0.055;

  return (
    <SceneShell accent={accent} dark>
      <SlideChip n={25} color={accent} appear={ap} label="Chat" />
      <SlideTitle
        text="The honest gap"
        subtitle="Casual chat does not yet teach EAKC anything — and that is openly tracked"
        color={accent}
        appear={ap}
      />

      {/* chat panel — a conversation happening */}
      <div
        style={{
          position: "absolute",
          top: 280,
          left: 132,
          width: 600,
          height: 200,
          opacity: spAppear(frame, fps, 12),
          borderRadius: 16,
          background: COLORS.bgCardLight,
          border: `2px solid ${COLORS.neonCyan}`,
          boxShadow: `0 0 34px ${COLORS.neonCyan}33`,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            height: 36,
            background: COLORS.bgCard,
            display: "flex",
            alignItems: "center",
            padding: "0 16px",
            fontSize: 13,
            fontFamily: FONTS.mono,
            color: COLORS.textMuted,
            flexShrink: 0,
          }}
        >
          💬 EAKC Chat · General · no agent attached
        </div>
        <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
          {bubbles.map((b, i) => (
            <div
              key={i}
              style={{
                alignSelf: b.me ? "flex-end" : "flex-start",
                maxWidth: 420,
                background: `${b.me ? COLORS.neonBlue : COLORS.neonCyan}1e`,
                border: `1.4px solid ${b.me ? COLORS.neonBlue : COLORS.neonCyan}77`,
                borderRadius: 12,
                padding: "8px 13px",
                fontSize: 14,
                color: COLORS.textSecondary,
                opacity: spAppear(frame, fps, 20 + i * 12),
              }}
            >
              {b.t}
            </div>
          ))}
        </div>
      </div>

      {/* consumption ripples radiating from the void */}
      {[0, 1, 2].map((k) => {
        const rp = (frame * 0.013 + k / 3) % 1;
        const r = 26 + rp * 130;
        return (
          <div
            key={`ripple-${k}`}
            style={{
              position: "absolute",
              left: voidX - r,
              top: voidY - r * 0.56,
              width: r * 2,
              height: r * 1.12,
              borderRadius: "50%",
              border: `2px solid ${COLORS.neonPurple}`,
              opacity: (1 - rp) * 0.5 * ap,
            }}
          />
        );
      })}

      {/* rotating event horizon */}
      <div
        style={{
          position: "absolute",
          left: voidX - 142,
          top: voidY - 82,
          width: 284,
          height: 164,
          borderRadius: "50%",
          border: `2px dashed ${COLORS.neonPurple}aa`,
          transform: `rotate(${frame * 0.5}deg)`,
          opacity: spAppear(frame, fps, 28),
        }}
      />

      {/* dark core */}
      <div
        style={{
          position: "absolute",
          left: voidX - 118,
          top: voidY - 66,
          width: 236,
          height: 132,
          borderRadius: "50%",
          background: `radial-gradient(ellipse, #000 0%, #000 30%, ${COLORS.neonPurple}66 64%, transparent 82%)`,
          boxShadow: `0 0 60px ${COLORS.neonPurple}55`,
          transform: `scale(${1 + Math.sin(frame * 0.06) * 0.06})`,
          opacity: spAppear(frame, fps, 22),
        }}
      />

      {/* chat turns funnelling in and being consumed */}
      {[0, 1, 2].map((i) => {
        const prog = (frame * 0.0072 + i * 0.34) % 1;
        const startX = voidX + (i - 1) * 96;
        const x = startX + (voidX - startX) * (prog * prog);
        const y = 484 + prog * (voidY - 484);
        const shrink = prog < 0.7 ? 1 : Math.max(0, 1 - (prog - 0.7) / 0.3);
        const op = Math.max(0, Math.min(prog / 0.1, (1 - prog) / 0.16, 1)) * ap;
        return (
          <div
            key={`turn-${i}`}
            style={{
              position: "absolute",
              left: x - 72,
              top: y - 14,
              width: 144,
              height: 28,
              transform: `scale(${shrink})`,
              opacity: op,
              borderRadius: 14,
              background: `${COLORS.neonCyan}26`,
              border: `1.4px solid ${COLORS.neonCyan}88`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontFamily: FONTS.mono,
              color: COLORS.textSecondary,
            }}
          >
            chat turn {i + 1}
          </div>
        );
      })}

      {/* nothing-is-captured label — pulsing */}
      <div
        style={{
          position: "absolute",
          left: voidX - 240,
          top: voidY + 74,
          width: 480,
          textAlign: "center",
          fontSize: 26,
          fontFamily: FONTS.heading,
          fontWeight: 900,
          color: COLORS.neonPurple,
          textShadow: glow(COLORS.neonPurple, true),
          transform: `scale(${pulse})`,
          opacity: spAppear(frame, fps, 40),
        }}
      >
        ∅  nothing is captured
      </div>
      <div
        style={{
          position: "absolute",
          left: voidX - 250,
          top: voidY + 120,
          width: 500,
          textAlign: "center",
          fontSize: 15,
          fontFamily: FONTS.mono,
          color: COLORS.textMuted,
          opacity: spAppear(frame, fps, 52),
        }}
      >
        15+ chat turns → no skills · no episodes · no memory
      </div>

      {/* two status cards */}
      <div style={{ position: "absolute", top: 300, right: 120, width: 700, display: "flex", flexDirection: "column", gap: 18 }}>
        <Card3D w={700} h={186} color={COLORS.neonGreen} frame={frame} delay={22} fps={fps} inline rotY={-7}>
          <div style={{ fontSize: 20, fontFamily: FONTS.heading, fontWeight: 900, color: COLORS.neonGreen, textShadow: glow(COLORS.neonGreen) }}>
            ✓ Chat WITH an agent attached
          </div>
          <div style={{ fontSize: 17, color: COLORS.textSecondary, marginTop: 10, lineHeight: 1.42 }}>
            Does feed the skill pipeline — but with a 24-hour delay and a job an
            operator still runs by hand. The loop works; it just isn't instant.
          </div>
        </Card3D>
        <Card3D w={700} h={186} color={COLORS.neonRed} frame={frame} delay={36} fps={fps} inline rotY={-7}>
          <div style={{ fontSize: 20, fontFamily: FONTS.heading, fontWeight: 900, color: COLORS.neonRed, textShadow: glow(COLORS.neonRed) }}>
            ✕ Plain chat on the Default agent
          </div>
          <div style={{ fontSize: 17, color: COLORS.textSecondary, marginTop: 10, lineHeight: 1.42 }}>
            A learning black hole — no skills, no episodes, no memory. Attaching an
            agent mid-conversation does not capture the earlier turns.
          </div>
        </Card3D>
      </div>

      <div
        style={{
          position: "absolute",
          top: 772,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Pill text="📋 TRACKED AS QUEUED ITEM P9 — chat as a first-class skill source" color={accent} />
      </div>

      <Punchline
        text={
          <>
            Named honestly — so it{" "}
            <span style={{ color: accent }}>doesn't disappear</span>.
          </>
        }
        color={accent}
        appear={spAppear(frame, fps, 84)}
      />
    </SceneShell>
  );
};

/* ════════════ Slide 26 — The flywheel ════════════ */
export const S26Flywheel: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonCyan;
  const ap = spAppear(frame, fps);

  const stages = [
    { icon: "🚀", t: "onboard", c: COLORS.neonBlue },
    { icon: "🛰️", t: "run missions", c: COLORS.neonCyan },
    { icon: "🔁", t: "skills produced", c: COLORS.neonGreen },
    { icon: "⚡", t: "auto-applied", c: COLORS.neonYellow },
    { icon: "📈", t: "better · cheaper · faster", c: COLORS.neonOrange },
    { icon: "➕", t: "more missions", c: COLORS.neonPink },
  ];
  const cx = 640;
  const cy = 540;
  const R = 232;
  const rot = frame * 0.7;

  return (
    <SceneShell accent={accent}>
      <SlideChip n={26} color={accent} appear={ap} label="The payoff" />
      <SlideTitle
        text="The flywheel"
        subtitle="Every mission makes the next one better — and that compounds"
        color={accent}
        appear={ap}
      />

      {/* spinning hub */}
      <div
        style={{
          position: "absolute",
          left: cx - 90,
          top: cy - 90,
          width: 180,
          height: 180,
          opacity: ap,
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: -30,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${accent}44 0%, transparent 70%)`,
            transform: `scale(${1 + Math.sin(frame * 0.08) * 0.12})`,
          }}
        />
        <div
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            background: `conic-gradient(${accent}, ${COLORS.neonPurple}, ${COLORS.neonGreen}, ${accent})`,
            transform: `rotate(${rot}deg)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 0 50px ${accent}88`,
          }}
        >
          <div
            style={{
              width: 132,
              height: 132,
              borderRadius: "50%",
              background: COLORS.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 56,
              transform: `rotate(${-rot}deg)`,
            }}
          >
            ⚙️
          </div>
        </div>
      </div>

      {/* rotating ring of stages */}
      {stages.map((s, i) => {
        const ang = ((rot + i * 60) * Math.PI) / 180;
        const x = cx + Math.cos(ang) * R;
        const y = cy + Math.sin(ang) * R;
        const sp = spring({ frame: frame - 14 - i * 9, fps, config: SPRING.bouncy });
        const op = interpolate(sp, [0, 1], [0, 1], clamp);
        const sc = interpolate(sp, [0, 1], [0.3, 1], clamp);
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: x - 96,
              top: y - 52,
              width: 192,
              height: 104,
              opacity: op,
              transform: `scale(${sc})`,
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: 16,
                background: `linear-gradient(140deg, ${s.c}30, ${COLORS.bgCard}f2)`,
                border: `2.2px solid ${s.c}`,
                boxShadow: `0 0 26px ${s.c}66`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 4,
              }}
            >
              <div style={{ fontSize: 34 }}>{s.icon}</div>
              <div
                style={{
                  fontSize: 14,
                  fontFamily: FONTS.mono,
                  fontWeight: 800,
                  color: s.c,
                  textAlign: "center",
                  textShadow: glow(s.c),
                }}
              >
                {s.t}
              </div>
            </div>
          </div>
        );
      })}

      {/* arrow to the moat */}
      <div
        style={{
          position: "absolute",
          left: 980,
          top: 470,
          fontSize: 60,
          color: accent,
          opacity: spAppear(frame, fps, 50),
          filter: `drop-shadow(0 0 14px ${accent})`,
        }}
      >
        ➜
      </div>
      <div style={{ position: "absolute", left: 1090, top: 388, width: 660 }}>
        <Card3D w={660} h={196} color={COLORS.neonPurple} frame={frame} delay={54} fps={fps} inline rotY={-8}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ fontSize: 56 }}>🕸️</div>
            <div>
              <div style={{ fontSize: 23, fontFamily: FONTS.heading, fontWeight: 900, color: COLORS.neonPurple, textShadow: glow(COLORS.neonPurple) }}>
                After 6 months — a skill graph
              </div>
              <div style={{ fontSize: 17, color: COLORS.textSecondary, marginTop: 8, lineHeight: 1.4 }}>
                Hundreds of curated lessons, specific to one organisation —
                impossible for a competitor to copy.
              </div>
            </div>
          </div>
        </Card3D>
      </div>

      <div style={{ position: "absolute", bottom: 50, left: 0, right: 0, display: "flex", justifyContent: "center" }}>
        <DefBox
          term="Skill graph"
          body="A tenant's accumulated set of skills. It is worth more next quarter than this one — and that growing value is the moat."
          color={accent}
          appear={spAppear(frame, fps, 76)}
          width={1120}
        />
      </div>
    </SceneShell>
  );
};

/* ════════════ Slide 27 — Long-term advantages ════════════ */
export const S27Advantages: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonGreen;
  const ap = spAppear(frame, fps);

  const cards = [
    { icon: "📣", c: COLORS.neonBlue, q: "Glean shipped a new agent product yesterday — update the comparison mission?" },
    { icon: "🧩", c: COLORS.neonCyan, q: "From your last 3 missions I learned skill X — applied to all research agents." },
    { icon: "💰", c: COLORS.neonYellow, q: "Today's summary cost $1.20 vs $0.30 — I drafted a cost-policy to switch to Flash." },
    { icon: "💡", c: COLORS.neonPink, q: "You mentioned 'Notion' three times this week — set up a Notion monitor mission?" },
    { icon: "🛡️", c: COLORS.neonOrange, q: "verify_output keeps rejecting 'placeholder' — promoting it to a tenant-wide rule." },
    { icon: "📊", c: COLORS.neonPurple, q: "Your skill graph has 47 entries — here's what EAKC has learned about your org." },
  ];

  return (
    <SceneShell accent={accent}>
      <SlideChip n={27} color={accent} appear={ap} label="The payoff" />
      <SlideTitle
        text="Long-term advantages"
        subtitle="EAKC stops only answering — and starts noticing"
        color={accent}
        appear={ap}
      />

      <div
        style={{
          position: "absolute",
          top: 282,
          left: 110,
          right: 110,
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 22,
          perspective: 1700,
        }}
      >
        {cards.map((c, i) => {
          const sp = spring({ frame: frame - 12 - i * 9, fps, config: SPRING.bouncy });
          const op = interpolate(sp, [0, 1], [0, 1], clamp);
          const sc = interpolate(sp, [0, 1], [0.55, 1], clamp);
          return (
            <div
              key={i}
              style={{
                height: 198,
                opacity: op,
                transform: `scale(${sc}) perspective(1200px) rotateY(${((i % 3) - 1) * 7}deg) translateZ(${Math.sin(frame * 0.03 + i) * 7}px)`,
                borderRadius: 16,
                background: `linear-gradient(140deg, ${c.c}24, ${COLORS.bgCard}f2)`,
                border: `2.2px solid ${c.c}`,
                boxShadow: `0 0 28px ${c.c}44`,
                padding: 20,
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ fontSize: 38 }}>{c.icon}</div>
                <div style={{ fontSize: 14, fontFamily: FONTS.mono, fontWeight: 800, color: c.c, letterSpacing: 1 }}>
                  PROACTIVE EAKC
                </div>
              </div>
              <div
                style={{
                  fontSize: 18,
                  fontFamily: FONTS.body,
                  color: COLORS.textPrimary,
                  lineHeight: 1.4,
                  fontStyle: "italic",
                }}
              >
                "{c.q}"
              </div>
            </div>
          );
        })}
      </div>

      <div
        style={{
          position: "absolute",
          top: 728,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          gap: 16,
          opacity: spAppear(frame, fps, 74),
        }}
      >
        <Pill text="🔍 TRANSPARENT — every skill is readable" color={COLORS.neonCyan} />
        <Pill text="↩️ REVERSIBLE — every patch is one-click Undo" color={COLORS.neonGreen} />
        <Pill text="⚡ NO SDK · NO 6-WEEK DEPLOY" color={COLORS.neonYellow} />
      </div>

      <div style={{ position: "absolute", bottom: 46, left: 0, right: 0, display: "flex", justifyContent: "center" }}>
        <PlainBox
          body="A tool waits to be asked. A colleague notices things, remembers, and tells you what's worth doing next. That's the shift."
          appear={spAppear(frame, fps, 88)}
          width={1180}
        />
      </div>
    </SceneShell>
  );
};

/* ════════════ Slide 28 — GitHub-grounded reality ════════════ */
export const S28Reality: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonLime;
  const ap = spAppear(frame, fps);

  const sources = [
    { logo: "github", name: "GitHub", note: "docs + code · verified", live: true, c: COLORS.neonGreen, y: 322 },
    { logo: "slack", name: "Slack", note: "designed · not yet tested", live: false, c: COLORS.textMuted, y: 462 },
    { logo: "jira", name: "Jira", note: "designed · not yet tested", live: false, c: COLORS.textMuted, y: 602 },
  ];

  return (
    <SceneShell accent={accent}>
      <SlideChip n={28} color={accent} appear={ap} label="The payoff" />
      <SlideTitle
        text="Where it stands today"
        subtitle="Proven on GitHub — docs and code — with more sources to come"
        color={accent}
        appear={ap}
      />

      {/* source → EAKC wiring */}
      <svg style={{ position: "absolute", inset: 0, pointerEvents: "none" }} width={1920} height={1080}>
        {sources.map((s, i) => (
          <path
            key={i}
            d={`M 560 ${s.y + 52} C 760 ${s.y + 52}, 880 470, 1080 470`}
            fill="none"
            stroke={s.live ? s.c : COLORS.textMuted}
            strokeWidth={s.live ? 4.5 : 2.6}
            strokeDasharray={s.live ? "0" : "8 9"}
            strokeDashoffset={-(frame * 3) % 17}
            strokeOpacity={spAppear(frame, fps, 40 + i * 8) * (s.live ? 0.95 : 0.5)}
            style={{ filter: s.live ? `drop-shadow(0 0 8px ${s.c})` : "none" }}
          />
        ))}
      </svg>

      {/* source nodes */}
      {sources.map((s, i) => {
        const sp = spring({ frame: frame - 14 - i * 12, fps, config: SPRING.bouncy });
        const op = interpolate(sp, [0, 1], [0, 1], clamp);
        const sc = interpolate(sp, [0, 1], [0.5, 1], clamp);
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              top: s.y,
              left: 180,
              width: 380,
              height: 104,
              opacity: op * (s.live ? 1 : 0.62),
              transform: `scale(${sc}) perspective(1000px) rotateY(7deg)`,
              borderRadius: 16,
              background: `linear-gradient(135deg, ${s.c}28, ${COLORS.bgCard}f0)`,
              border: `2.4px ${s.live ? "solid" : "dashed"} ${s.c}`,
              boxShadow: s.live ? `0 0 34px ${s.c}77` : "none",
              display: "flex",
              gap: 16,
              alignItems: "center",
              padding: "0 22px",
            }}
          >
            <div
              style={{
                width: 58,
                height: 58,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                filter: s.live ? `drop-shadow(0 0 10px ${s.c}88)` : "grayscale(0.9)",
              }}
            >
              <BrandLogo name={s.logo} size={52} />
            </div>
            <div>
              <div style={{ fontSize: 26, fontFamily: FONTS.heading, fontWeight: 900, color: s.c, textShadow: s.live ? glow(s.c) : "none" }}>
                {s.name}
              </div>
              <div style={{ fontSize: 15, fontFamily: FONTS.mono, color: COLORS.textMuted, marginTop: 4 }}>
                {s.note}
              </div>
            </div>
            <div style={{ marginLeft: "auto", fontSize: 14, fontFamily: FONTS.mono, fontWeight: 800, color: s.live ? COLORS.neonGreen : COLORS.textMuted }}>
              {s.live ? "● LIVE" : "○ NEXT"}
            </div>
          </div>
        );
      })}

      {/* EAKC node */}
      <div
        style={{
          position: "absolute",
          left: 1080,
          top: 348,
          width: 300,
          height: 246,
          opacity: spAppear(frame, fps, 28),
          transform: `scale(${spAppear(frame, fps, 28)})`,
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: -34,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${accent}44 0%, transparent 70%)`,
            transform: `scale(${1 + Math.sin(frame * 0.07) * 0.12})`,
          }}
        />
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            borderRadius: 22,
            background: `linear-gradient(145deg, ${accent}30, ${COLORS.bgCard}f4)`,
            border: `3px solid ${accent}`,
            boxShadow: `0 0 50px ${accent}77`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          <BrandLogo name="eakc" size={84} style={{ filter: `drop-shadow(0 0 12px ${accent})` }} />
          <div style={{ fontSize: 26, fontFamily: FONTS.heading, fontWeight: 900, color: accent, textShadow: glow(accent, true) }}>
            EAKC
          </div>
          <div style={{ fontSize: 14, fontFamily: FONTS.mono, color: COLORS.textMuted }}>learns from any source</div>
        </div>
      </div>

      {/* takeaway */}
      <div style={{ position: "absolute", top: 358, right: 130, width: 340 }}>
        <Card3D w={340} h={230} color={accent} frame={frame} delay={50} fps={fps} inline rotY={-9}>
          <div style={{ fontSize: 20, fontFamily: FONTS.heading, fontWeight: 900, color: accent, textShadow: glow(accent) }}>
            The skills layer is source-agnostic
          </div>
          <div style={{ fontSize: 17, color: COLORS.textSecondary, marginTop: 12, lineHeight: 1.44 }}>
            Every new connector inherits the same learning loop. Adding Slack or
            Jira is additive — not a rebuild.
          </div>
        </Card3D>
      </div>

      <div style={{ position: "absolute", bottom: 50, left: 0, right: 0, display: "flex", justifyContent: "center" }}>
        <PlainBox
          body="So far this is proven on GitHub repositories — docs and code together. Slack and Jira are next, and the learning machinery won't change to fit them."
          appear={spAppear(frame, fps, 80)}
          width={1180}
        />
      </div>
    </SceneShell>
  );
};

/* ════════════ Slide 29 — Demo gallery ════════════ */
export const S29Gallery: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonPink;
  const ap = spAppear(frame, fps);

  const frames = [
    { label: "Chat nudge", c: COLORS.neonPink },
    { label: "/admin/learned panel", c: COLORS.neonBlue },
    { label: "Skill reader side-panel", c: COLORS.neonPurple },
    { label: "Mission-run skill timeline", c: COLORS.neonGreen },
    { label: "Memory & token inspector", c: COLORS.neonCyan },
    { label: "Default persona switcher", c: COLORS.neonAmber },
  ];

  return (
    <SceneShell accent={accent}>
      <SlideChip n={29} color={accent} appear={ap} label="The payoff" />
      <SlideTitle
        text="Demo gallery"
        subtitle="Reserved frames — drop in the real implementation screenshots"
        color={accent}
        appear={ap}
      />

      <div
        style={{
          position: "absolute",
          top: 296,
          left: 116,
          right: 116,
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gridTemplateRows: "repeat(2, 1fr)",
          gap: 30,
          height: 600,
        }}
      >
        {frames.map((f, i) => (
          <SnapshotFrame
            key={i}
            inline
            w={540}
            h={282}
            color={f.c}
            frame={frame}
            delay={14 + i * 9}
            fps={fps}
            rotY={((i % 3) - 1) * 6}
            label={f.label}
            url="eakc.app"
          />
        ))}
      </div>

      <div style={{ position: "absolute", bottom: 44, left: 0, right: 0, display: "flex", justifyContent: "center" }}>
        <PlainBox
          body="Each frame is a placeholder for a real product screenshot. Drop the image into the project's public folder and the demo section is done."
          appear={spAppear(frame, fps, 84)}
          width={1160}
        />
      </div>
    </SceneShell>
  );
};

/* ════════════ Slide 30 — Closing ════════════ */
export const S30Closing: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonYellow;

  const titleSp = spring({ frame, fps, config: SPRING.bouncy });
  const titleOp = interpolate(titleSp, [0, 1], [0, 1], clamp);
  const titleSc = interpolate(titleSp, [0, 1], [0.6, 1], clamp);
  const lineOp = spAppear(frame, fps, 34);
  const recapOp = spAppear(frame, fps, 56);
  const dateOp = spAppear(frame, fps, 76);

  const pages = [...Array(9)];

  return (
    <SceneShell accent={accent}>
      {/* fanning pages behind the book */}
      <div style={{ position: "absolute", top: 250, left: "50%", width: 0, height: 0 }}>
        {pages.map((_, i) => {
          const sp = spring({ frame: frame - 10 - i * 5, fps, config: SPRING.gentle });
          const spread = interpolate(sp, [0, 1], [0, 1], clamp);
          const ang = (i - 4) * 17 * spread;
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                left: -34,
                top: -10,
                fontSize: 84,
                opacity: spread * 0.85,
                transform: `rotate(${ang}deg) translateY(${-spread * 150}px)`,
                transformOrigin: "50% 220px",
                filter: `drop-shadow(0 0 14px ${accent}aa)`,
              }}
            >
              📄
            </div>
          );
        })}
        <div
          style={{
            position: "absolute",
            left: -75,
            top: -40,
            fontSize: 150,
            opacity: titleOp,
            transform: `scale(${titleSc})`,
            filter: `drop-shadow(0 0 34px ${accent})`,
          }}
        >
          📕
        </div>
      </div>

      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          paddingTop: 150,
        }}
      >
        <div
          style={{
            fontSize: 78,
            fontFamily: FONTS.heading,
            fontWeight: 900,
            color: COLORS.textPrimary,
            textAlign: "center",
            letterSpacing: -1.5,
            lineHeight: 1.12,
            opacity: titleOp,
            transform: `scale(${titleSc})`,
            textShadow: `0 0 44px ${accent}cc`,
          }}
        >
          A tool you buy gets <span style={{ color: COLORS.neonRed }}>old</span>.
          <br />A colleague you grow gets <span style={{ color: accent }}>better</span>.
        </div>

        <div
          style={{
            fontSize: 25,
            fontFamily: FONTS.body,
            color: COLORS.textSecondary,
            marginTop: 22,
            opacity: lineOp,
            fontWeight: 500,
          }}
        >
          EAKC learns your organisation — in writing, reversibly, a little more every week.
        </div>

        <div
          style={{
            display: "flex",
            gap: 14,
            marginTop: 30,
            opacity: recapOp,
            transform: `translateY(${(1 - recapOp) * 22}px)`,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <Pill text="ONE SKILLS TABLE" color={COLORS.neonCyan} />
          <Pill text="12 KINDS" color={COLORS.neonPurple} />
          <Pill text="AUTO-APPLY + UNDO" color={COLORS.neonGreen} />
          <Pill text="TRANSPARENT & REVERSIBLE" color={COLORS.neonPink} />
        </div>

        <div style={{ marginTop: 30, opacity: dateOp }}>
          <Pill
            text="Building Hermes-Inspired Capabilities into EAKC  ·  24 MAY 2026"
            color={accent}
            solid
            size={20}
          />
        </div>
      </div>
    </SceneShell>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
